import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Auth } from 'aws-amplify';
import { SalesDataValidations } from './types/UploadResponse';
import { Permission, User } from './types/User';
import { Dealer } from './types/Dealer';
import { DealerGroup } from './types/DealerGroup';
import { SalesDataPartition } from './types/SalesDataPartition';
import { SalesDataUpload } from './types/SalesDataUpload';
import { SalesDataMapping } from './types/SalesDataMapping';
import { AuthConfig } from './types/AuthConfig';
import { AdvertiserActivity, AdvertiserActivityResponse } from './types/AdvertiserActivity';
import { UserEdit } from './types/UserEdit';
import { AMCWorkflow, AMCWorkflowExecution, AMCWorkflowExecutionRequest } from './types/AMCData';
import { TradeInMarket } from './types/TradeInMarket';
import { TradeInOffer } from './types/TradeInOffer';
import { DealerOfferResult } from './types/DealerOfferResult';
import { ValuationReportSummary } from './types/ValuationReportSummary';
import { OfferSummaryDTO } from './types/OfferSummaryDTO';
import { AmcInstance, AwsAmcInstance, ProvisionDetails } from './types/AmcInstance';
import { AmcOwningEntity } from './types/AmcOwningEntity';
import { AmcUploadDataSetCriteria } from './types/Criteria/AmcUploadDataSetCriteria';
import { AmcUploadDataSet } from './types/AmcUploadDataSet';
import { DealerOverview } from './types/DealerOverview';
import { JobStatus } from './types/JobStatus';
import { Dashboard } from './types/Dashboard';
import { MappedDataSummary } from './types/MappedDataSummary';
import { DealerImportSite, ImportSite } from './types/DealerImportSite';
import { ForecastingParameters } from './types/AdvertiserRequest';
import { Vertical } from './types/Vertical';
import { SasAccount } from './types/SasAccount';
import {
    ApiResponse,
    AudienceMapping,
    AudienceMappingOptions,
    DmaMapping,
    DspAdGroupDict,
    Dv360Advertiser,
    DspAdvertiser,
    DspAudienceCategory,
    DspEntity,
    DspInventorySupply,
    DspMacro,
    DspMacroOptions,
    ListAudienceResult,
    OemProgram,
    OemProgramDto,
    OemProgramOptions,
    ShellCampaign,
} from './types/DspEntity';
import { Agency, ExportSetting } from './types/Agency';
import { AmcAttribution } from './types/AmcAttribution';
import { DealerExportSite, ExportSite } from './types/ExportSite';
import { Workflow } from './types/Workflow';
import { Asset, SizmekAd } from './types/Sizmek';
import { ReleaseNote } from './types/ReleaseNote';
import { BuildInfo } from './types/BuildInfo';
import { Code } from './types/Code';
import { AmcAudience } from './types/AmcAudiences';
import { AmcReachAndFrequency, AmcReachAndFrequencyCriteria } from './types/AmcReachAndFrequency';
import { AdvertiserDataSetSummary, DataSet } from './types/DataSet';
import {
    Campaign,
    CampaignAdGroup,
    CampaignChangeRequest,
    CampaignCriteria,
    CampaignFlight,
    CampaignMetaSyncResult,
    FlightSyncResult,
    CampaignStatusCount,
    CpmValue,
    GeoLocation,
    ValidateDspCampaignResponse,
} from './types/Campaign';
import { CampaignDefaultSetting, CampaignDefaultSettingCriteria } from './types/CampaignDefaultSetting';
import { Brand } from './types/Brand';
import { Oem } from './types/Oem';
import {
    AttributionPerformance,
    CampaignPerformanceCriteria,
    CampaignPerformanceListCriteria,
    PerformanceListCriteria,
    PerformanceMetric,
} from './types/CampaignPerformance';
import { AttributionStatus, PolkDealer, PolkDealerDealer, PolkDealerSearch, PolkSalesWorkflowSet } from './types/Polk';
import { AmcDataSet } from './types/AmcDataSet';
import { AdCreative } from './types/AmazonAds';
import { AdServerImportSource, AdServerPlacement, FtCampaign, FtPlacement } from './types/FtCampaign';
import { FileInfo } from './types/S3';
import { AppBySpendSummaryListCriteria } from './types/Analytics/AppBySpendSummaryListCriteria';
import { ConversionSummaryListCriteria } from './types/Analytics/ConversionSummaryListCriteria';
import { AppBySpendBySiteNameSummary } from './types/Analytics/AppBySpendSummary';
import { ConversionByConversionNameSummary } from './types/Analytics/ConversionSummary';
import { Metric } from './types/Analytics/Metric';
import { PerformanceCriteria } from './types/Analytics/PerformanceCriteria';
import { PerformanceEntity } from './types/Analytics/PerformanceEntity';
import { PerformanceEntityCriteria } from './types/Analytics/PerformanceEntityCriteria';
import { AmcInstanceListCriteria } from './types/AMC/AmcInstanceListCriteria';
import { AgencyCriteria } from './types/Criteria/AgencyCriteria';
import { DealerListCriteria } from './types/Dealer/DealerListCriteria';
import { DspAudienceCriteria } from './types/Criteria/DspAudienceCriteria';
import { DspInventorySourceCriteria } from './types/Criteria/DspInventorySourceCriteria';
import { DspGeoLocationCriteria } from './types/Criteria/DspGeoLocationCriteria';
import { PolkSalesWorkflowSetCriteria } from './types/Criteria/PolkSalesWorkflowSetCriteria';
import { Pageable } from './types/Pageable';
import {
    AdTagImportSource,
    AdTagPlacement,
    AdTagPlacementData,
    AdTagTemplate,
    AdTagType,
    Project,
} from './types/AdTag';
import { AxiosPageableResponse } from './types/AxiosPageableResponse';
import { DspAudience } from './types/DspAudience';
import { DspProductCategory } from './types/DspProductCategory';
import { InventorySource as DspInventorySource } from './types/Inventory';
import { DspInventorySourceDeal } from './types/DspInventorySourceDeal';
import { DspInventorySourceExchange } from './types/DspInventorySourceExchange';
import { DspGeoLocation } from './types/DspGeoLocation';
import { CreativePixel } from './types/HAT/CreativePixel';
import { CreativePixelCriteria } from './types/Criteria/CreativePixelCriteria';
import { HatConfig } from './types/HAT/HatConfig';
import { Tracking } from './types/HAT/Tracking';
import Utils from './components/Utils';
import { AttributionCriteria } from './types/Criteria/AttributionCriteria';
import { CampaignSyncResponse } from './types/Responses/CampaignSyncResponse';
import { DspProductCategoryCriteria } from './types/Criteria/DspProductCategoryCriteria';
import { CampaignAdGroupCriteria } from './types/Criteria/CampaignAdGroupCriteria';

import pako from 'pako';
import { BrandCriteria } from './types/Criteria/BrandCriteria';
import { OemCriteria } from './types/Criteria/OemCriteria';
import { OemProgramCriteria } from './types/Criteria/OemProgramCriteria';
import { AgencyMarketRegionCriteria } from './types/Criteria/AgencyMarketRegionCriteria';
import { EmailTemplateDTO, SendTestEmailRequest, SendTestEmailResponse, PreviewEmailResponse } from './types/EmailTest';

axios.interceptors.request.use(function (request) {
    if (sessionStorage.getItem('token')) {
        request.headers.common.Authorization = 'Bearer ' + sessionStorage.getItem('token');
    }
    return request;
});

axios.interceptors.response.use(
    function (response) {
        return response;
    },
    function (error) {
        // If the request was cancelled, skip further error handling.
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }
        if (error.response.status === 401) {
            return Auth.currentSession()
                .then((session) => {
                    sessionStorage.setItem('token', session.getIdToken().getJwtToken());
                    error.config.headers.Authorization = 'Bearer ' + sessionStorage.getItem('token');
                    return axios.request(error.config);
                })
                .catch((error) => {
                    return Promise.reject(error);
                });
        } else {
            return Promise.reject(error);
        }
    }
);

const BYPASS_WAIT_INDICATOR = { headers: { XBusy: 'false' } };
const CUSTOM_ERROR_HANDLER = { headers: { XCustomErrorHandler: 'true' } };

export function request<T>(config: AxiosRequestConfig, signal?: AbortSignal): Promise<AxiosResponse<T>> {
    const source = axios.CancelToken.source();

    if (signal) {
        if (signal.aborted) {
            source.cancel('aborted');
        }
        signal.addEventListener('abort', () => source.cancel('aborted'), { once: true });
    }

    return axios.request<T>({
        ...config,
        cancelToken: source.token,
    });
}

type RequestOptions = { signal?: AbortSignal };

type RequestConfig = RequestOptions & AxiosRequestConfig;

export type { AdvertiserActivity, AdvertiserActivityResponse };

const ApiService = {
    getAppConfig: function (): Promise<AxiosResponse> {
        return axios.get<AuthConfig>('/api/auth/config');
    },

    uploadShellCampaignFile: (
        formData: FormData,
        agencyId: number | undefined,
        fileType: string
    ): Promise<AxiosResponse<ApiResponse>> => {
        return axios.post('/api/campaign/upload-bulk-campaigns', formData, {
            params: {
                agencyId: agencyId,
                fileType: fileType,
            },
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    getUiBuildConfig: function (): Promise<AxiosResponse<BuildInfo>> {
        return axios.get('/buildinfo.json');
    },

    getApiBuildConfig: function (): Promise<AxiosResponse> {
        return axios.get('/api/auth/buildInfo');
    },

    getCodes: function (codeGroup: string): Promise<AxiosResponse<Code[]>> {
        return axios.get('/api/code/' + codeGroup, BYPASS_WAIT_INDICATOR);
    },

    processAttributionExport: function (formData: FormData): Promise<AxiosResponse> {
        return axios.post('/api/attribution/export', formData);
    },

    /**
     * OEM
     */
    getOems: function (criteria?: OemCriteria, pageable?: Pageable): Promise<AxiosResponse<Oem[]>> {
        return axios.get('/api/oems', {
            params: {
                ...criteria,
                ...pageable,
                ids: criteria?.ids?.join(',') || undefined,
                oemProgramIds: criteria?.oemProgramIds?.join(',') || undefined,
                agencyIds: criteria?.agencyIds?.join(',') || undefined,
                advertiserIds: criteria?.advertiserIds?.join(',') || undefined,
            },
        });
    },

    getPaginatedOems(page: number, size: number, sortModel: any): Promise<AxiosPageableResponse<Oem>> {
        const sort = sortModel.map((sort: any) => `${sort.field},${sort.sort}`).join(',');
        return axios.get('/api/oems/filter', {
            params: { page, size, sort },
        });
    },

    getOemBrands: function (oemId: number): Promise<AxiosResponse<Brand[]>> {
        return axios.get(`/api/oems/${oemId}/brands`);
    },

    /**
     * Brands
     */
    getBrands: function (criteria?: BrandCriteria): Promise<AxiosResponse<Brand[]>> {
        return axios.get('/api/brands', {
            params: {
                ...criteria,
                oemIds: criteria?.oemIds?.join(',') || undefined,
            },
        });
    },

    /**
     * CRM Data
     */
    uploadSalesData: function (formData: FormData): Promise<AxiosResponse> {
        return axios.post<SalesDataValidations>('/api/salesdata/upload', formData);
    },

    updateSalesDataUpload: function (salesDataUpload: SalesDataUpload): Promise<AxiosResponse> {
        return axios.put('/api/salesdata/upload', salesDataUpload);
    },

    getDataSets: function (): Promise<AxiosResponse<DataSet[]>> {
        return axios.get('/api/salesdata/datasets');
    },

    deleteSalesData: function (salesDataUploadId: number): Promise<AxiosResponse> {
        return axios.delete('/api/salesdata/' + salesDataUploadId);
    },

    downloadSalesData: function (salesDataUploadId: number): Promise<AxiosResponse> {
        return axios.get('/api/salesdata/download/' + salesDataUploadId);
    },

    getSalesDataPartitionsBySalesDataUpload: function (
        salesDataId: number | null | undefined
    ): Promise<AxiosResponse<SalesDataPartition[]>> {
        return axios.get('/api/salesDataPartition/', {
            params: {
                salesDataId: salesDataId,
            },
        });
    },

    getSalesDataUpload: function (salesDataUploadId: number): Promise<AxiosResponse<SalesDataUpload>> {
        return axios.get(`/api/salesdata/uploads/${salesDataUploadId}`);
    },

    getSalesDataUploadsByDealer: function (
        agencyId: number | null,
        dealerId: number | null | undefined
    ): Promise<AxiosResponse<SalesDataUpload[]>> {
        const dealerParm =
            dealerId != null ? 'dealerId=' + dealerId : '' + (agencyId != null ? '&agencyId=' + agencyId : '');
        return axios.get('/api/salesdata/uploadsByDealer?' + dealerParm);
    },

    searchSalesDataUploads: function (
        attributionCriteria: AttributionCriteria,
        dateFilter: Date | null | undefined
    ): Promise<AxiosResponse<SalesDataUpload[]>> {
        let params: string[] = [];

        if (dateFilter != null) {
            params.push(`dateFilter=${dateFilter.toLocaleDateString('sv-SE')}`);
        }

        const dealerParm = params.join('&');
        return axios.post('/api/salesdata/searchSalesDataUploads?' + dealerParm, attributionCriteria);
    },

    getSalesDataMapping: function (salesDataUploadId: number): Promise<AxiosResponse<SalesDataMapping>> {
        return axios.get<SalesDataMapping>('/api/salesdata/mapping/' + salesDataUploadId).then((response) => {
            if (!response.data) return Promise.reject();
            return response;
        });
    },

    updateSalesDataMapping: function (mapping: SalesDataMapping): Promise<AxiosResponse> {
        return axios.post('/api/salesdata/mapping', mapping);
    },

    getMappedDataSummary: function (
        salesDataUploadId: number,
        salesDataMapping: SalesDataMapping
    ): Promise<AxiosResponse<MappedDataSummary>> {
        return axios.post<MappedDataSummary>(
            '/api/salesdata/mapping/sampleMappedData/' + salesDataUploadId,
            salesDataMapping
        );
    },

    queueForAMC: function (salesDataUpload: SalesDataUpload): Promise<AxiosResponse> {
        return axios.post('/api/salesdata/queue/', salesDataUpload);
    },

    /**
     * Users
     */
    getCurrentUser: function (): Promise<AxiosResponse<User>> {
        return axios.get<User>('/api/users/current');
    },

    getUser: function (userId: number): Promise<AxiosResponse<User>> {
        return axios.get<User>('/api/users/user/' + userId);
    },

    updateUser: function (user: User): Promise<AxiosResponse<User>> {
        return axios.post<User>('/api/users/user', user);
    },

    toggleUserEnabled: function (user: User): Promise<AxiosResponse> {
        return axios.post('/api/users/user/toggleEnabled', user);
    },

    resetUserPassword(user: User): Promise<AxiosResponse> {
        return axios.post('/api/users/user/resetPassword', user);
    },

    getUsers: function (): Promise<AxiosResponse<User[]>> {
        return axios.get<User[]>('/api/users');
    },

    getUserEdit: function (userId: number): Promise<AxiosResponse<UserEdit>> {
        return axios.get('/api/users/useredit/' + userId);
    },

    /**
     * Permissions
     */
    getPermissions: function (userType: string): Promise<AxiosResponse<Permission[]>> {
        return axios.get('/api/users/permissions/' + userType);
    },

    /**
     * Dealers / Advertisers
     */
    getDealers: function (): Promise<AxiosResponse<Dealer[]>> {
        return axios.get('/api/dealers', BYPASS_WAIT_INDICATOR);
    },

    getDealersFtAdvertiserIdIsNotNull: function (): Promise<AxiosResponse<Dealer[]>> {
        return axios.get('/api/dealers/getDealersFtAdvertiserIdIsNotNull');
    },

    getDealersForAMCInstance: function (amcInstanceId: number): Promise<AxiosResponse<Dealer[]>> {
        return axios.get('/api/dealers/amcInstance/' + amcInstanceId);
    },

    getAttributionPerformanceList: function (
        criteria: PerformanceListCriteria,
        config?: RequestConfig
    ): Promise<AxiosResponse<AttributionPerformance[]>> {
        return request<AttributionPerformance[]>(
            {
                ...BYPASS_WAIT_INDICATOR,
                ...config,
                method: 'POST',
                url: '/api/dealers/amcAttribution/list',
                data: {
                    ...criteria,
                    startDate: Utils.toStartOfDayISOString(criteria.startDate),
                    endDate: Utils.toEndOfDayISOString(criteria.endDate),
                },
            },
            config?.signal
        );
    },

    getDealer: function (dealerId: number): Promise<AxiosResponse<Dealer>> {
        return axios.get('/api/dealers/' + dealerId);
    },

    getDefaultDealer: function (): Promise<AxiosResponse<Dealer>> {
        return axios.get('/api/dealers/defaultDealer');
    },

    getDealerByName: function (dealerName: string): Promise<AxiosResponse<Dealer>> {
        return axios.get('/api/dealers/name/' + dealerName);
    },

    isDuplicate: function (dealer: Dealer): Promise<AxiosResponse<Dealer[]>> {
        return axios.post('/api/dealers/isduplicate', dealer, BYPASS_WAIT_INDICATOR);
    },

    getDealerByDspAdvertiserId(dspAdvertiserId: string): Promise<AxiosResponse<Dealer[]>> {
        return axios.get('/api/dealers/dspAdvertiser/' + dspAdvertiserId, BYPASS_WAIT_INDICATOR);
    },

    getDealerByDv360AdvertiserId(dv360AdvertiserId: string): Promise<AxiosResponse<Dealer[]>> {
        return axios.get('/api/dealers/dv360Advertiser/' + dv360AdvertiserId, BYPASS_WAIT_INDICATOR);
    },

    getDv360Advertiser(dv360AdvertiserId: string): Promise<AxiosResponse<Dv360Advertiser>> {
        return axios.get('/api/dealers/getDv360Advertiser/' + dv360AdvertiserId, BYPASS_WAIT_INDICATOR);
    },

    // TODO: check here
    getDspAdvertiser(dspEntityId: number, dspAdvertiserId: string): Promise<AxiosResponse<DspAdvertiser>> {
        return axios.get(
            '/api/dealers/dspAdvertiser?dspEntityId=' + dspEntityId + '&dspAdvertiserId=' + dspAdvertiserId,
            BYPASS_WAIT_INDICATOR
        );
    },

    getDealerOverview: function (dealerId: number): Promise<AxiosResponse<DealerOverview>> {
        return axios.get('/api/dealers/' + dealerId + '/overview');
    },

    updateDealer: function (dealer: Dealer): Promise<AxiosResponse<Dealer>> {
        return axios.post('/api/dealers/', dealer, CUSTOM_ERROR_HANDLER);
    },

    createSizmekAdvertiser: function (dealer: Dealer): Promise<AxiosResponse<Dealer>> {
        return axios.post('/api/dealers/sizmekAdvertiser', dealer);
    },

    getAutoExportCount(
        dealerId: number,
        operator: string,
        age: number,
        newUsedFilter: string,
        certifiedFilter: string,
        customLabelFilter: string,
        imageFilter: string
    ) {
        return axios.post(
            '/api/dealers/autoexportcount',
            {
                dealerId: dealerId,
                ageExportFilterOperator: operator,
                ageExportFilterValue: age,
                newUsedFilter: newUsedFilter,
                certifiedFilter: certifiedFilter,
                customLabelFilter: customLabelFilter,
                imageFilter: imageFilter,
            },
            BYPASS_WAIT_INDICATOR
        );
    },

    uploadDealerImage(formData: FormData) {
        return axios.post('/api/dealers/image', formData);
    },

    getAssignedDealers: function (userId?: number): Promise<AxiosResponse<Dealer[]>> {
        let url = '/api/salesdata/assignedDealers';
        if (userId) {
            url += '?userId=' + userId;
        }
        return axios.get<Dealer[]>(url, BYPASS_WAIT_INDICATOR);
    },

    getAdvertiser: (advertiserId: number): Promise<AxiosResponse<Dealer>> => {
        return axios.get(`/api/advertiser/${advertiserId}`, BYPASS_WAIT_INDICATOR);
    },

    getAdvertiserActivity: function (): Promise<AxiosResponse<AdvertiserActivityResponse>> {
        return axios.get('/api/advertiser/activity', BYPASS_WAIT_INDICATOR);
    },

    refreshAdvertiserActivity: function (): Promise<AxiosResponse<AdvertiserActivityResponse>> {
        return axios.post('/api/advertiser/activity/refresh');
    },

    getAllDealers: function (
        page: number,
        name: string,
        agencyId: number | null,
        size = 50
    ): Promise<AxiosPageableResponse<Dealer>> {
        return axios.get(`/api/dealers/all`, { ...BYPASS_WAIT_INDICATOR, params: { page, size, name, agencyId } });
    },

    getDealerList: function (criteria: DealerListCriteria): Promise<AxiosResponse<Dealer[]>> {
        return axios.post('/api/dealers/list', criteria);
    },

    getVerticals: function (): Promise<AxiosResponse<Vertical[]>> {
        return axios.get('/api/dealers/verticals');
    },

    getSasAccounts: function (): Promise<AxiosResponse<SasAccount[]>> {
        return axios.get('/api/dealers/sasAccounts');
    },

    getDspEntities: function (): Promise<AxiosResponse<DspEntity[]>> {
        return axios.get('/api/dealers/dspEntities');
    },

    /**
     * Dealer Groups
     */
    getDealerGroups: function (): Promise<AxiosResponse<DealerGroup[]>> {
        return axios.get('/api/dealerGroups');
    },

    createDealerGroup: function (data: Partial<DealerGroup>): Promise<AxiosResponse<DealerGroup>> {
        return axios.post('/api/dealerGroups', data);
    },

    /**
     * DSP Audiences
     */

    getDspAudiences(
        dealerId: number,
        categoryPath: string[],
        nextToken: string | null | undefined
    ): Promise<AxiosResponse<ListAudienceResult>> {
        return axios.post('/api/dspAudience/', {
            dealerId: dealerId,
            categoryPath: categoryPath,
            nextToken: nextToken,
        });
    },

    getDspAudienceCategories: function (
        dealerId: number,
        categoryPath: string[]
    ): Promise<AxiosResponse<DspAudienceCategory[]>> {
        return axios.post('/api/dspAudience/category', {
            dealerId: dealerId,
            categoryPath: categoryPath,
        });
    },

    getDspProductCategories: (criteria?: DspProductCategoryCriteria): Promise<AxiosResponse<DspProductCategory[]>> => {
        return axios.post('/api/dsp/productCategories', criteria || {});
    },

    getDspInventorySources: (criteria?: DspInventorySourceCriteria): Promise<AxiosResponse<DspInventorySource[]>> => {
        return axios.get('/api/dsp/inventorySources', {
            params: {
                ...(criteria || {}),
                ids: criteria?.ids?.join(',') || undefined,
                inventorySourceTypes: criteria?.inventorySourceTypes?.join(',') || undefined,
            },
        });
    },

    getDspInventorySourceExchanges: (): Promise<AxiosResponse<DspInventorySourceExchange[]>> => {
        return axios.get('/api/dsp/inventorySourceExchanges');
    },

    getDspInventorySourceDeals: (): Promise<AxiosResponse<DspInventorySourceDeal[]>> => {
        return axios.get('/api/dsp/inventorySourceDeals');
    },

    getDspGeoLocations: (criteria?: DspGeoLocationCriteria): Promise<AxiosResponse<DspGeoLocation[]>> => {
        return axios.post('/api/dsp/geoLocations', criteria || {});
    },

    getDspAudienceSources: (criteria?: DspAudienceCriteria): Promise<AxiosResponse<DspAudience[]>> => {
        return axios.get('/api/dsp/audiences', {
            params: {
                ...(criteria || {}),
                ids: criteria?.ids?.join(',') || undefined,
                platforms: criteria?.platforms?.join(',') || undefined,
                categories: criteria?.categories?.join(',') || undefined,
                subCategories: criteria?.subCategories?.join(',') || undefined,
            },
        });
    },

    /**
     * AMC Reach and Frequency
     */
    getAmcReachAndFrequencyList: function (
        criteria: AmcReachAndFrequencyCriteria,
        config?: RequestConfig
    ): Promise<AxiosResponse<AmcReachAndFrequency[]>> {
        return request<AmcReachAndFrequency[]>(
            {
                ...config,
                method: 'POST',
                url: '/api/amcReachAndFrequency/list',
                data: {
                    ...criteria,
                    ...(criteria.startDate !== undefined
                        ? { startDate: Utils.toStartOfDayISOString(criteria.startDate) }
                        : {}),
                    ...(criteria.endDate !== undefined ? { endDate: Utils.toEndOfDayISOString(criteria.endDate) } : {}),
                },
            },
            config?.signal
        );
    },

    /**
     * AMC Instances
     */
    getAmcInstances: function (): Promise<AxiosResponse<AmcInstance[]>> {
        return axios.get('/api/amc/amcInstances');
    },

    getAmcInstanceList: function (
        criteria: AmcInstanceListCriteria,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse<AmcInstance[]>> {
        return axios.post('/api/amc/amcInstances', criteria, { ...config });
    },

    getAwsAmcInstances: function (): Promise<AxiosResponse<AwsAmcInstance[]>> {
        return axios.get('/api/amc/awsAmcInstances');
    },

    updateAmcInstance: function (amcInstance: AmcInstance): Promise<AxiosResponse> {
        return axios.post('/api/amc/amcInstance', amcInstance);
    },

    deleteAmcInstance: function (amcInstanceId: number): Promise<AxiosResponse> {
        return axios.delete('/api/amc/amcInstance/' + amcInstanceId);
    },

    provisionAmcInstance: function (provisionDetails: ProvisionDetails): Promise<AxiosResponse<AmcInstance>> {
        return axios.post('/api/amc/amcInstance/provision', provisionDetails);
    },

    getAmcDataSets: function (amcInstanceId: number): Promise<AxiosResponse<AmcDataSet[]>> {
        return axios.get('/api/amc/amcInstance/' + amcInstanceId + '/datasets');
    },

    /**
     * AMC Audiences
     */
    getAmcAudiences: function (amcInstanceId: number): Promise<AxiosResponse<AmcAudience[]>> {
        return axios.get('/api/amc/amcInstance/' + amcInstanceId + '/audiences');
    },

    createAmcAudience: function (dealerId: number, dataSetId: string): Promise<AxiosResponse<AmcAudience>> {
        return axios.post('/api/amc/dealer/' + dealerId + '/dataset/' + dataSetId + '/audience');
    },

    getWorkflows: function (config?: AxiosRequestConfig): Promise<AxiosResponse<Workflow[]>> {
        return axios.get('/api/workflow', { ...config });
    },
    saveWorkflow: function (workflow: Workflow): Promise<AxiosResponse<Workflow>> {
        return axios.post('/api/workflow', workflow);
    },
    deleteWorkflow: function (workflow: Workflow): Promise<AxiosResponse> {
        return axios.delete('/api/workflow/' + workflow.id);
    },

    getAmcWorkflows: function (
        amcInstanceId: number,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse<AMCWorkflow[]>> {
        return axios.get(`/api/amc/${amcInstanceId}/workflows`, { ...config });
    },

    saveAmcWorkflow: function (amcInstanceId: number, workflow: AMCWorkflow, isNew: boolean): Promise<AxiosResponse> {
        return axios.put('/api/amc/' + amcInstanceId + '/workflow?isNew=' + isNew, workflow);
    },

    deleteAmcWorkflow: function (amcInstanceId: number, workflowId: string): Promise<AxiosResponse> {
        return axios.delete('/api/amc/' + amcInstanceId + '/workflow/' + workflowId);
    },

    runWorkflow: function (
        amcInstanceId: number,
        workflowRequest: AMCWorkflowExecutionRequest
    ): Promise<AxiosResponse<AMCWorkflowExecution>> {
        return axios.post<AMCWorkflowExecution>('/api/amc/' + amcInstanceId + '/workflow/run', workflowRequest);
    },

    getWorkflowExecutions: function (
        amcInstanceId: number,
        workflowExecutionId: string
    ): Promise<AxiosResponse<AMCWorkflowExecution[]>> {
        return axios.get<AMCWorkflowExecution[]>(
            '/api/amc/' + amcInstanceId + '/workflowExecutions/' + workflowExecutionId
        );
    },

    getWorkflowExecutionResultFile: function (
        amcInstanceId: number,
        workflowExecutionId: string
    ): Promise<AxiosResponse> {
        return axios.get('/api/amc/' + amcInstanceId + '/workflowExecutionResults/' + workflowExecutionId);
    },

    getDataSetDetails: function (dealerId: number, dataSetName: string): Promise<AxiosResponse> {
        return axios.get('/api/amc/dataSetDetails?dealerId=' + dealerId + '&dataSetName=' + dataSetName);
    },

    getDspEntitiesByAgencyId: function (agencyId: number): Promise<AxiosResponse<DspEntity[]>> {
        return axios.get('/api/dealers/dspEntity?agencyId=' + agencyId);
    },

    getDataSetSummary: function (
        agencyId: number | null,
        dealerId: number | null | undefined,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse<AdvertiserDataSetSummary[]>> {
        const params: Record<string, any> = {
            dealerId: dealerId ? dealerId : '',
            agencyId: agencyId ? agencyId : '',
        };

        return axios.get('/api/amc/dataSetSummary', { ...config, params });
    },

    getAdvertiserDataSetSummary: function (dealerId: number): Promise<AxiosResponse<AdvertiserDataSetSummary[]>> {
        return axios.get('/api/amc/dataSetSummary/dealer/' + dealerId, BYPASS_WAIT_INDICATOR);
    },

    refreshAllAmcAudiences: function (): Promise<AxiosResponse<void>> {
        return axios.post('/api/amc/amcAudiences/refreshAll');
    },

    getCustomAudiences: function (
        agencyId: number | null,
        dealerId: number | null,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse<AdvertiserDataSetSummary[]>> {
        const params: Record<string, any> = {
            dealerId: dealerId ? dealerId : '',
            agencyId: agencyId ? agencyId : '',
        };
        return axios.get('/api/amc/customAudiences', { ...config, params });
    },

    /**
     * AMC Owning Entities
     */
    getAmcOwningEntities: function (): Promise<AxiosResponse<AmcOwningEntity[]>> {
        return axios.get('/api/amcOwningEntities');
    },

    updateAmcOwningEntity: function (amcOwningEntity: AmcOwningEntity): Promise<AxiosResponse> {
        return axios.post('/api/amcOwningEntities', amcOwningEntity);
    },

    deleteAmcOwningEntity: function (amcOwningEntityId: number): Promise<AxiosResponse> {
        return axios.delete('/api/amcOwningEntities/' + amcOwningEntityId);
    },

    /**
     * AMC Upload Data Sets
     */
    getAdvertiserAmcUploadDataSets: function (advertiserId: number): Promise<AxiosResponse<AmcUploadDataSet[]>> {
        return axios.get(`/api/amc/datasets/advertiser/${advertiserId}`);
    },

    getAmcUploadDataSets: function (criteria: AmcUploadDataSetCriteria): Promise<AxiosResponse<AmcUploadDataSet[]>> {
        return axios.post(`/api/amc/datasets`, criteria);
    },

    /**
     * AMC Offline Attribution
     */
    getAmcAttributions: function (
        agencyId: number | null,
        dealerId: number | null | undefined,
        pageable: Pageable,
        config?: AxiosRequestConfig
    ): Promise<AxiosPageableResponse<AmcAttribution>> {
        const params: Record<string, any> = {
            dealerId: dealerId ? dealerId : '',
            agencyId: agencyId ? agencyId : '',
            ...pageable,
            sort: `${pageable.sortBy},${pageable.sort}`,
            sortBy: undefined,
        };

        return axios.get('/api/amcAttribution', { ...config, params });
    },

    getAmcAttribution: function (id: number): Promise<AxiosResponse<AmcAttribution>> {
        return axios.get('/api/amcAttribution/' + id);
    },

    updateAmcAttribution(amcAttribution: AmcAttribution): Promise<AxiosResponse<AmcAttribution>> {
        return axios.post('/api/amcAttribution', amcAttribution);
    },

    deleteAmcAttribution(id: number): Promise<AxiosResponse> {
        return axios.delete('/api/amcAttribution/' + id);
    },

    /**
     * Trade-Ins / Offers
     */
    getTradeInMarkets: function (): Promise<AxiosResponse<TradeInMarket[]>> {
        return axios.get('/api/dealers/tradeInMarkets');
    },

    getOffers: function (dealerId: number): Promise<AxiosResponse<DealerOfferResult>> {
        return axios.get('/api/offers/dealer/' + dealerId);
    },

    uploadOffers: function (dealerId: number, formData: FormData): Promise<AxiosResponse> {
        return axios.post('/api/offers/dealer/' + dealerId, formData);
    },

    updateOffer: function (offer: TradeInOffer): Promise<AxiosResponse> {
        return axios.post('/api/offers/offer/' + offer.id, offer);
    },

    deleteOffer: function (dealerId: number, offer: TradeInOffer): Promise<AxiosResponse> {
        return axios.delete('/api/offers/dealer/' + dealerId + '/offer/' + offer.id);
    },

    runValuationReport: function (mode: string): Promise<AxiosResponse<ValuationReportSummary>> {
        return axios.get('/api/offers/valuationReport/' + mode);
    },

    runOfferExport: function (): Promise<AxiosResponse> {
        return axios.get('/api/offers/export');
    },

    getOfferSummary: function (dealerId: number): Promise<AxiosResponse<OfferSummaryDTO>> {
        return axios.get('/api/offers/dealer/' + dealerId + '/offerSummary');
    },

    /**
     * Admin calls
     */
    // Job Status Api calls
    getFailedJobCount: function (): Promise<AxiosResponse<number>> {
        return axios.get('/api/admin/jobStatus/failedJobCount', BYPASS_WAIT_INDICATOR);
    },

    getJobStatus: function (): Promise<AxiosResponse<JobStatus[]>> {
        return axios.get('/api/admin/jobStatus', BYPASS_WAIT_INDICATOR);
    },

    acknowledgeFailedJob: function (jobId: number): Promise<AxiosResponse> {
        return axios.post('/api/admin/jobStatus/acknowledge/' + jobId, {}, BYPASS_WAIT_INDICATOR);
    },

    // Release Notes
    getReleaseNotes: function (): Promise<AxiosResponse<ReleaseNote[]>> {
        return axios.get('/api/admin/releaseNotes', BYPASS_WAIT_INDICATOR);
    },

    getReleaseNote: function (id: number): Promise<AxiosResponse<ReleaseNote>> {
        return axios.get('/api/admin/releaseNotes/' + id, BYPASS_WAIT_INDICATOR);
    },

    getLastPublishedReleaseNote: function (): Promise<AxiosResponse<ReleaseNote | null>> {
        return axios.get('/api/admin/releaseNotes/lastPublished');
    },

    updateReleaseNote: function (releaseNote: ReleaseNote): Promise<AxiosResponse<ReleaseNote>> {
        return axios.post('/api/admin/releaseNotes', releaseNote);
    },

    scheduleReleaseNote: function (releaseNote: ReleaseNote): Promise<AxiosResponse<ReleaseNote>> {
        return axios.post('/api/admin/releaseNotes/schedule', releaseNote);
    },

    deleteReleaseNote: function (id: number): Promise<AxiosResponse> {
        return axios.delete('/api/admin/releaseNotes/' + id);
    },

    getQuickSightDashboards: function (): Promise<AxiosResponse<Dashboard[]>> {
        return axios.get('/api/quicksight/dashboards');
    },

    getQuickSightDashboard: function (): Promise<AxiosResponse<Dashboard>> {
        return axios.get('/api/quicksight/dashboard', BYPASS_WAIT_INDICATOR);
    },

    updateDefaultDashboard: function (dashboardId: string | null): Promise<AxiosResponse> {
        return axios.post('/api/quicksight/dashboard/' + dashboardId);
    },

    getQuickSightDashboardUrl: function (dashboardId: string): Promise<AxiosResponse<string>> {
        return axios.get('/api/quicksight/dashboard/' + dashboardId, BYPASS_WAIT_INDICATOR);
    },

    getAnalyticsDocs: function (): Promise<AxiosResponse> {
        return axios.get('/api/analytics/apidoc');
    },

    /**
     * Import Sites, Dealer Import Sites
     */
    getAvailableImportSites: function (dealerId: number): Promise<AxiosResponse<ImportSite[]>> {
        return axios.get('/api/importSites/dealer/' + dealerId);
    },

    getImportSitesForDealer: function (dealerId: number): Promise<AxiosResponse<DealerImportSite[]>> {
        return axios.get('/api/importSites/dealerImportSites/dealer/' + dealerId);
    },

    updateDealerImportSite: function (dealerImportSite: DealerImportSite): Promise<AxiosResponse> {
        return axios.post('/api/importSites/dealerImportSite/', dealerImportSite);
    },

    deleteDealerImportSite: function (dealerId: number, importSiteId: number): Promise<AxiosResponse> {
        return axios.delete('/api/importSites/dealer/' + dealerId + '/importSite/' + importSiteId);
    },

    getDealerImportSites: function (): Promise<AxiosResponse<DealerImportSite[]>> {
        return axios.get('/api/importSites/dealerImportSites');
    },

    /**
     * Export Sites, Dealer Export Sites
     */
    getAvailableExportSites: function (dealerId: number): Promise<AxiosResponse<ExportSite[]>> {
        return axios.get('/api/exportSites/dealer/' + dealerId);
    },

    getExportSitesForDealer: function (dealerId: number): Promise<AxiosResponse<DealerExportSite[]>> {
        return axios.get('/api/exportSites/dealerExportSites/dealer/' + dealerId);
    },

    getDealerExportSites: function (): Promise<AxiosResponse<DealerExportSite[]>> {
        return axios.get('/api/exportSites/dealerExportSites');
    },

    updateDealerExportSite: function (dealerExportSite: DealerExportSite): Promise<AxiosResponse> {
        return axios.post('/api/exportSites/dealerExportSite/', dealerExportSite);
    },

    getExportData: function (dealerId: number, exportSiteId: number): Promise<AxiosResponse> {
        return axios.get('/api/exportSites/dealer/' + dealerId + '/exportSite/' + exportSiteId);
    },

    deleteExportData: function (dealerId: number, exportSiteId: number): Promise<AxiosResponse> {
        return axios.delete('/api/exportSites/dealer/' + dealerId + '/exportSite/' + exportSiteId);
    },

    getForecastingParameters: function (): Promise<AxiosResponse<ForecastingParameters>> {
        return axios.get('/api/dealerRequest/forecastingParameters');
    },

    /**
     * Agencies
     */
    getAllAgencies: (
        criteria: AgencyCriteria,
        { page = 1, ...pageable }: Pageable
    ): Promise<AxiosPageableResponse<Agency>> => {
        return axios.get('/api/agency/all', {
            headers: { XBusy: 'false' },
            params: {
                ...criteria,
                ...pageable,
                page: page - 1,
                ids: criteria?.ids?.join(',') || undefined,
            },
        });
    },

    getAgencies: function (): Promise<AxiosResponse> {
        return axios.get('/api/agency', BYPASS_WAIT_INDICATOR);
    },

    getAgency: function (id: number): Promise<AxiosResponse<Agency>> {
        return axios.get('/api/agency/' + id);
    },

    getAgencyExportSetting: function (id: number): Promise<AxiosResponse<ExportSetting>> {
        return axios.get(`/api/export/${id}/setting`);
    },

    getExportType: function (): Promise<AxiosResponse<[]>> {
        return axios.get('/api/export/types');
    },

    createAdhocExport: function (exportSetting: ExportSetting): Promise<AxiosResponse<void>> {
        return axios.post('/api/export/adhoc-export', exportSetting);
    },

    createExportSettings: function (
        agencyId: number,
        sources?: string,
        sourcesOndemand?: string,
        platforms?: string,
        platformsOndemand?: string
    ): Promise<AxiosResponse<[]>> {
        return axios.post('/api/export/setting', { agencyId, sources, sourcesOndemand, platforms, platformsOndemand });
    },

    getAgencyForUser: function (): Promise<AxiosResponse<Agency>> {
        return axios.get('/api/agency/currentuser');
    },

    updateAgency(agency: Agency): Promise<AxiosResponse> {
        return axios.post('/api/agency', agency);
    },

    uploadAgencyImage(formData: FormData) {
        return axios.post('/api/agency/image', formData);
    },

    getAgencyAvailableDealers: function (id: number): Promise<AxiosResponse<Dealer[]>> {
        return axios.get('/api/agency/' + id + '/availabledealers');
    },

    getAgencyDspAdvertiserNames: function (id: number): Promise<AxiosResponse<Record<string, string>>> {
        return axios.get('/api/agency/' + id + '/dspAdvertisers');
    },

    getAgencyHatTrackingStatuses: function (id: number): Promise<AxiosResponse<Record<string, string>>> {
        return axios.get('/api/agency/' + id + '/hatTrackingStatuses');
    },

    getAgencyDealers: function (id: number): Promise<AxiosResponse<Dealer[]>> {
        return axios.get('/api/agency/' + id + '/dealers');
    },

    getAgencyByHost: function (host: string): Promise<AxiosResponse<Agency>> {
        return axios.get('/api/agency/host/' + host);
    },

    verifySenderDomain: function (agencyId: number, senderEmail: string): Promise<AxiosResponse> {
        return axios.post(
            '/api/agency/' + agencyId + '/sender-email/verify?senderEmail=' + encodeURIComponent(senderEmail),
            null,
            CUSTOM_ERROR_HANDLER
        );
    },

    getSenderDomainStatus: function (agencyId: number, senderEmail: string): Promise<AxiosResponse> {
        return axios.get(
            '/api/agency/' + agencyId + '/sender-email/status?senderEmail=' + encodeURIComponent(senderEmail),
            CUSTOM_ERROR_HANDLER
        );
    },

    sendTestSenderEmail: function (agencyId: number): Promise<AxiosResponse> {
        return axios.post('/api/agency/' + agencyId + '/sender-email/test', null, CUSTOM_ERROR_HANDLER);
    },

    /**
     * Campaigns (replacement for CampaignRequest / CampaignOrder - 4.0
     */
    getCampaigns: function (campaignCriteria: CampaignCriteria): Promise<AxiosResponse<Campaign[]>> {
        return axios.post('/api/campaign/search', campaignCriteria);
    },

    getCampaignsForDealer: function (dealerId: number): Promise<AxiosResponse<Campaign[]>> {
        return axios.get('/api/campaign/dealer/' + dealerId, BYPASS_WAIT_INDICATOR);
    },

    getCampaign: function (campaignId: number, config?: AxiosRequestConfig): Promise<AxiosResponse<Campaign>> {
        return axios.get('/api/campaign/' + campaignId, { ...config });
    },

    deleteCampaign(campaignId: number): Promise<AxiosResponse> {
        return axios.delete('/api/campaign/' + campaignId);
    },

    archiveCampaign(campaignId: number): Promise<AxiosResponse> {
        return axios.get('/api/campaign/' + campaignId + '/archive');
    },

    updateFlight: function (campaignFlight: CampaignFlight): Promise<AxiosResponse> {
        return axios.post('/api/campaign/flight', campaignFlight);
    },
    /** Ask the ETL to re-aggregate every day of a flight (super admins; one request per flight per hour). */
    syncFlight: function (flightRowId: number): Promise<AxiosResponse<FlightSyncResult>> {
        return axios.post(`/api/campaign/flight/${flightRowId}/sync`);
    },
    /** Pull the campaign's flights from Iceberg dsp_meta into RDS now (super admins; Amazon DSP only). */
    syncCampaignFlightsFromMeta: function (campaignId: number): Promise<AxiosResponse<CampaignMetaSyncResult>> {
        return axios.post(`/api/campaign/${campaignId}/sync-flights-from-meta`);
    },

    createFlight: function (
        campaignId: number,
        flightData: Pick<CampaignFlight, 'startDateTime' | 'endDateTime' | 'budgetAmount'>
    ): Promise<AxiosResponse> {
        return axios.post(`/api/campaign/${campaignId}/flight/create`, flightData);
    },

    notifyGatewayDelay: function (campaignId: number): Promise<AxiosResponse> {
        return axios.post(`/api/campaign/${campaignId}/notifyGatewayDelay`);
    },

    refreshDspStatus: function (campaignId: number): Promise<AxiosResponse<Campaign>> {
        return axios.post('/api/campaign/refreshDspStatus/' + campaignId);
    },

    updateCampaign: function (campaign: Campaign, files?: File[]): Promise<AxiosResponse<Campaign>> {
        const formData = new FormData();
        formData.append('campaignJson', JSON.stringify(campaign));
        const MAX_FILE_SIZE = 314572800;
        if (files) {
            files.forEach((file) => {
                if (file.size < MAX_FILE_SIZE) {
                    formData.append('file', file, file.name);
                }
            });
        }

        return axios.post('/api/campaign', formData);
    },

    getCampaignCreatives: function (id: number, changeRequestId?: number): Promise<AxiosResponse<string[]>> {
        return axios.get(
            '/api/campaign/id/' + id + '/creatives' + (changeRequestId ? '?changeRequestId=' + changeRequestId : ''),
            BYPASS_WAIT_INDICATOR
        );
    },

    getCampaignCreative: function (id: number, fileName: string): Promise<AxiosResponse> {
        return axios.post(
            '/api/campaign/creative',
            { id: id, fileName: encodeURI(fileName) },
            { responseType: 'blob' }
        );
    },

    findDuplicateCampaigns: function (
        currentCampaignId: number | null,
        dealerId?: number,
        mediaType?: string,
        campaignType?: string,
        strategyType?: string,
        startDate?: Date | null,
        endDate?: Date | null,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse<Campaign[]>> {
        let params: Record<string, any> = {
            dealerId: dealerId,
            mediaType: mediaType,
            campaignType: campaignType,
            strategyType: strategyType,
        };

        if (currentCampaignId) {
            params.currentCampaignId = currentCampaignId;
        }

        if (startDate) {
            params.startDate = startDate.toISOString();
        }

        if (endDate) {
            params.endDate = endDate.toISOString();
        }

        return axios.get('/api/campaign/duplicates', { ...config, params: params });
    },

    validateDspCampaign: function (
        dealerId: number,
        dspCampaignId: string
    ): Promise<AxiosResponse<ValidateDspCampaignResponse>> {
        return axios.get('/api/campaign/dspCampaign?dealerId=' + dealerId + '&dspCampaignId=' + dspCampaignId);
    },

    getCampaignStatusCounts(campaignCriteria: CampaignCriteria): Promise<AxiosResponse<CampaignStatusCount[]>> {
        return axios.post('/api/campaign/statusCounts', campaignCriteria);
    },

    getGeoLocations: function (campaignId: number): Promise<AxiosResponse<GeoLocation[]>> {
        return axios.get(`/api/campaign/${campaignId}/geoLocations`, BYPASS_WAIT_INDICATOR);
    },

    getCampaignChangeRequestPending: function (campaignId: number): Promise<AxiosResponse<CampaignChangeRequest>> {
        return axios.get('/api/campaign/' + campaignId + '/campaignChangeRequestPending');
    },

    getCampaignChangeRequests: function (campaignId: number): Promise<AxiosResponse<CampaignChangeRequest[]>> {
        return axios.get('/api/campaign/' + campaignId + '/campaignChangeRequests');
    },

    getCampaignChangeRequest: function (
        campaignId: number,
        changeRequestId: number | null
    ): Promise<AxiosResponse<CampaignChangeRequest>> {
        return axios.get(
            '/api/campaign/' +
                campaignId +
                '/campaignChangeRequest' +
                (changeRequestId ? '?changeRequestId=' + changeRequestId : '')
        );
    },

    updateCampaignChangeRequest: function (
        campaignChangeRequest: CampaignChangeRequest,
        files: File[]
    ): Promise<AxiosResponse<CampaignChangeRequest>> {
        const formData = new FormData();
        const jsonString = JSON.stringify(campaignChangeRequest);
        const compressed = pako.gzip(jsonString);
        const blob = new Blob([compressed], { type: 'application/gzip' });
        formData.append('campaignJson', blob);
        const MAX_FILE_SIZE = 314572800;
        if (files) {
            files.forEach((file) => {
                if (file.size < MAX_FILE_SIZE) {
                    formData.append('file', file, file.name);
                }
            });
        }

        return axios.post('/api/campaign/campaignChangeRequest', formData);
    },

    updateCampaignChangeRequestComplete: function (
        campaignId: number,
        changeRequestId: number
    ): Promise<AxiosResponse<Campaign>> {
        return axios.post('/api/campaign/' + campaignId + '/campaignChangeRequest/' + changeRequestId + '/complete');
    },

    declineCampaignChangeRequest: function (
        campaignId: number,
        changeRequestId: number
    ): Promise<AxiosResponse<Campaign>> {
        return axios.post('/api/campaign/' + campaignId + '/campaignChangeRequest/' + changeRequestId + '/decline');
    },

    updateCampaignComplete: function (campaignId: number): Promise<AxiosResponse<Campaign>> {
        return axios.post('/api/campaign/' + campaignId + '/complete');
    },

    getCampaignPerformance: function (
        campaignPerformanceCriteria: CampaignPerformanceCriteria
    ): Promise<AxiosResponse<PerformanceMetric[]>> {
        return axios.post('/api/campaignPerformance', campaignPerformanceCriteria, BYPASS_WAIT_INDICATOR);
    },

    getCampaignPerformanceList: function (
        criteria: CampaignPerformanceListCriteria,
        config?: RequestConfig
    ): Promise<AxiosResponse<PerformanceMetric[]>> {
        return request<PerformanceMetric[]>(
            {
                ...BYPASS_WAIT_INDICATOR,
                ...config,
                method: 'POST',
                url: '/api/campaignPerformance/list',
                data: {
                    ...criteria,
                    startDate: Utils.toStartOfDayISOString(criteria.startDate),
                    endDate: Utils.toEndOfDayISOString(criteria.endDate),
                },
            },
            config?.signal
        );
    },

    getCampaignPerformanceListByDataSet: function (
        criteria: CampaignPerformanceListCriteria,
        config?: RequestConfig
    ): Promise<AxiosResponse<PerformanceMetric[]>> {
        return request<PerformanceMetric[]>(
            {
                ...BYPASS_WAIT_INDICATOR,
                ...config,
                method: 'POST',
                url: '/api/campaignPerformance/listByDataSet',
                data: {
                    ...criteria,
                    startDate: Utils.toStartOfDayISOString(criteria.startDate),
                    endDate: Utils.toEndOfDayISOString(criteria.endDate),
                },
            },
            config?.signal
        );
    },

    getCampaignPerformanceActiveMediaTypes: function (
        campaignPerformanceListCriteria: CampaignPerformanceListCriteria,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse<string[]>> {
        return axios.post(
            '/api/campaignPerformance/activeMediaTypes',
            {
                ...campaignPerformanceListCriteria,
                startDate: Utils.toStartOfDayISOString(campaignPerformanceListCriteria.startDate),
                endDate: Utils.toEndOfDayISOString(campaignPerformanceListCriteria.endDate),
            },
            { ...config, ...BYPASS_WAIT_INDICATOR }
        );
    },

    processCampaignPerformanceExport: function (formData: FormData): Promise<AxiosResponse> {
        return axios.post('/api/campaignPerformance/export', formData);
    },

    getCampaignCPMValues: function (dealerId: number): Promise<AxiosResponse<CpmValue[]>> {
        return axios.get('/api/campaign/' + dealerId + '/cpm');
    },

    getCampaignAdGroups: function (dealerId: number): Promise<AxiosResponse<CampaignAdGroup[]>> {
        return axios.get('/api/campaign/getCampaignAdGroups/' + dealerId);
    },

    getOldestStartDateOfAdGroup: function (dealerId: number): Promise<AxiosResponse<string>> {
        return axios.get('/api/campaign/getOldestStartDateOfAdGroup?dealerId=' + dealerId);
    },

    getCampaignAdGroupsByDealerAfterDate: function (
        dealerId: number,
        yearMonth: string
    ): Promise<AxiosResponse<CampaignAdGroup[]>> {
        return axios.get(
            '/api/campaign/getCampaignAdGroupsByDealerAfterDate?dealerId=' + dealerId + '&yearMonth=' + yearMonth
        );
    },

    getCampaignAdGroupsByCriteria: (criteria?: CampaignAdGroupCriteria): Promise<AxiosResponse<CampaignAdGroup[]>> => {
        return axios.post('/api/campaignAdGroups', criteria || {});
    },

    updateAdGroupMediaType: (id: number, mediaType: string): Promise<AxiosResponse<CampaignAdGroup>> => {
        return axios.patch(`/api/campaignAdGroups/${id}/mediaType`, null, { params: { mediaType } });
    },

    updateCampaignMediaType: (id: number, mediaType: string): Promise<AxiosResponse<Campaign>> => {
        return axios.patch(`/api/campaign/${id}/mediaType`, null, { params: { mediaType } });
    },

    /**
     * Campaign Default Settings
     */
    getCampaignDefaultSettings: function (
        campaignDefaultSettingCriteria: CampaignDefaultSettingCriteria
    ): Promise<AxiosResponse<CampaignDefaultSetting[]>> {
        return axios.post('/api/campaignDefaultSetting', campaignDefaultSettingCriteria, BYPASS_WAIT_INDICATOR);
    },

    /**
     * Home Page Dashboard
     */

    getDealersForUser: function (): Promise<AxiosResponse<Dealer[]>> {
        return axios.get('/api/home/dealers', BYPASS_WAIT_INDICATOR);
    },

    getDealerAds: function (dealerId: number): Promise<AxiosResponse<SizmekAd[]>> {
        return axios.get('/api/dealers/' + dealerId + '/ads', BYPASS_WAIT_INDICATOR);
    },

    getDealerDspAdCreatives: function (dealerId: number): Promise<AxiosResponse<AdCreative[]>> {
        return axios.get('/api/dealers/' + dealerId + '/dspAdCreatives', BYPASS_WAIT_INDICATOR);
    },

    getDealerDspAsset: function (dealerId: number, assetId: string): Promise<AxiosResponse<AdCreative[]>> {
        return axios.get(`/api/dealers/${dealerId}/dspAsset/${assetId}`, BYPASS_WAIT_INDICATOR);
    },

    getAsset: function (dealerId: number, assetId: number): Promise<AxiosResponse<Asset>> {
        return axios.get('/api/dealers/' + dealerId + '/asset/' + assetId);
    },

    getCampaignAdCreatives: function (campaignId: number): Promise<AxiosResponse<AdCreative[]>> {
        return axios.get(`/api/campaign/${campaignId}/adCreatives`, BYPASS_WAIT_INDICATOR);
    },
    getCampaignAdGroupAdCreatives: function (campaignAdGroupId: number): Promise<AxiosResponse<AdCreative[]>> {
        return axios.get(`/api/campaignAdGroups/${campaignAdGroupId}/adCreatives`, BYPASS_WAIT_INDICATOR);
    },

    /**
     * Polk Sales and Audiences
     */
    getPolkDealerDealers(dealerId: number): Promise<AxiosResponse<PolkDealerDealer[]>> {
        return axios.get('/api/polkSales/polkDealerDealers/' + dealerId);
    },

    getPolkSalesWorkflowSets(
        criteria: PolkSalesWorkflowSetCriteria,
        config?: RequestConfig
    ): Promise<AxiosResponse<PolkSalesWorkflowSet[]>> {
        return request<PolkSalesWorkflowSet[]>(
            {
                ...config,
                method: 'POST',
                url: '/api/polkSales/workflowSets',
                data: {
                    ...criteria,
                    advertiserIds: criteria.advertiserIds,
                    startDate: Utils.toStartOfDayISOString(criteria.startDate),
                    endDate: Utils.toEndOfDayISOString(criteria.endDate),
                },
            },
            config?.signal
        );
    },

    getPolkSales(dbFilters: string, config?: AxiosRequestConfig): Promise<AxiosResponse<any[]>> {
        return axios.get('/api/polkSales/getPolkSales' + dbFilters, config);
    },

    getSalesData(dbFilters: string, config?: AxiosRequestConfig): Promise<AxiosResponse<any[]>> {
        return axios.get('/api/polkSales/getSalesData' + dbFilters, config);
    },

    getMakeAndModel(): Promise<AxiosResponse<any[]>> {
        return axios.get('/api/polkSales/getMakeAndModel');
    },

    getPurchaseType(): Promise<AxiosResponse<any[]>> {
        return axios.get('/api/polkSales/getPurchaseType');
    },

    getFuelType(): Promise<AxiosResponse<any[]>> {
        return axios.get('/api/polkSales/getFuelType');
    },

    getSegments(): Promise<AxiosResponse<any[]>> {
        return axios.get('/api/polkSales/getSegments');
    },

    getPolkSalesGeographyCities(): Promise<AxiosResponse<any[]>> {
        return axios.get('/api/polkSales/getGeographyCities');
    },

    updatePolkDealerDealers(dealerId: number, polkDealerDealers: PolkDealerDealer[]): Promise<AxiosResponse> {
        return axios.post('/api/polkSales/polkDealerDealers/' + dealerId, polkDealerDealers);
    },

    participatePolkDealerDealers(dealerId: number): Promise<AxiosResponse> {
        return axios.post(`/api/polkSales/polkDealerDealers/${dealerId}/participate`);
    },

    searchPolkDealer(dealerSearch: PolkDealerSearch): Promise<AxiosResponse<PolkDealer[]>> {
        return axios.post('/api/polkSales/dealers', dealerSearch);
    },

    runAttribution(attributionRequest: { date: Date; dealerId: number }): Promise<AxiosResponse> {
        return axios.post('/api/polkSales/runAttribution', attributionRequest);
    },

    getAttributionStatus(dealerId: number): Promise<AxiosResponse<AttributionStatus>> {
        return axios.get('/api/polkSales/' + dealerId + '/attributionStatus');
    },

    getPolkSalesData(amcUploadId: number): Promise<AxiosResponse> {
        return axios.get('/api/polkSales/download/' + amcUploadId);
    },

    getDealerPolkAudienceData(): Promise<AxiosResponse> {
        return axios.get('/api/polkAudience/dealers');
    },

    getDealerPolkAudienceQueueData(): Promise<AxiosResponse> {
        return axios.get('/api/polkAudience/queue');
    },

    getAllAdGroupDicts(): Promise<AxiosResponse> {
        return axios.get('/api/dspAdGroupDict/getAllAdGroupDicts');
    },

    getDspMacroPlatforms(): Promise<AxiosResponse<string[]>> {
        return axios.get('/api/dsp-macro/platforms');
    },

    getDspMacros(page: number, size: number, sortModel: any): Promise<AxiosPageableResponse<DspMacro>> {
        const sort = sortModel.map((sort: any) => `${sort.field},${sort.sort}`).join(',');
        return axios.get('/api/dsp-macro', {
            params: { page, size, sort },
        });
    },

    getDmaMappings(
        page: number,
        size: number,
        sortModel: any,
        search?: string
    ): Promise<AxiosPageableResponse<DmaMapping>> {
        const sort = sortModel.map((sort: any) => `${sort.field},${sort.sort}`).join(',');
        return axios.get('/api/dma-mapping', {
            params: { page, size, sort, search: search || undefined },
        });
    },

    getDmaDescriptions(): Promise<AxiosResponse<DmaMapping[]>> {
        return axios.get('/api/dma-mapping/descriptions');
    },

    getDmaMappingsByZip(zip: string): Promise<AxiosResponse<DmaMapping[]>> {
        return axios.get(`/api/dma-mapping/by-zip/${zip}`);
    },

    getPublishedReleaseNotes(page: number, size: number, sortModel: any): Promise<AxiosPageableResponse<ReleaseNote>> {
        const sort = sortModel.map((sort: any) => `${sort.field},${sort.sort}`).join(',');
        return axios.get('/api/release-notes', {
            params: { page, size, sort },
        });
    },

    getAudienceMappings(page: number, size: number, sortModel: any): Promise<AxiosPageableResponse<AudienceMapping>> {
        const sort = sortModel.map((sort: any) => `${sort.field},${sort.sort}`).join(',');
        return axios.get('/api/audience-mapping', {
            params: { page, size, sort },
        });
    },

    getAudienceMappingOptions(): Promise<AxiosResponse<AudienceMappingOptions>> {
        return axios.get('/api/audience-mapping/options');
    },

    getDspMacroOptions(): Promise<AxiosResponse<DspMacroOptions>> {
        return axios.get('/api/dsp-macro/options');
    },

    getOemProgramOptions(): Promise<AxiosResponse<OemProgramOptions>> {
        return axios.get('/api/oem-program/options');
    },

    getOemPrograms(page: number, size: number, sortModel: any): Promise<AxiosPageableResponse<OemProgramDto>> {
        const sort = sortModel.map((sort: any) => `${sort.field},${sort.sort}`).join(',');
        return axios.get('/api/oem-program', {
            params: { page, size, sort },
        });
    },

    getBulkUpdateCampaigns(
        page: number,
        size: number,
        sortModel: any,
        agencyId: number | undefined | null
    ): Promise<AxiosPageableResponse<ShellCampaign>> {
        const sort = sortModel.map((sort: any) => `${sort.field},${sort.sort}`).join(',');
        return axios.get('/api/campaign/bulk-update-campaigns', {
            params: { page, size, sort, agencyId: agencyId },
        });
    },

    getBulkCreateCampaigns(page: number, size: number, sortModel: any): Promise<AxiosPageableResponse<ShellCampaign>> {
        const sort = sortModel.map((sort: any) => `${sort.field},${sort.sort}`).join(',');
        return axios.get('/api/campaign/bulk-create-campaigns', {
            params: { page, size, sort },
        });
    },

    getDspInventorySupplies(
        page: number,
        size: number,
        sortModel: any
    ): Promise<AxiosPageableResponse<DspInventorySupply>> {
        const sort = sortModel.map((sort: any) => `${sort.field},${sort.sort}`).join(',');
        return axios.get('/api/dsp-inventory-supply', {
            params: { page, size, sort },
        });
    },
    updateAudienceMapping(data: AudienceMapping): Promise<AxiosResponse> {
        const updatedData = {
            ...data,
            strategyTypeId: data.strategyType.id, // Update existing or add new fields
        };
        return axios.put('/api/audience-mapping/' + updatedData.id, updatedData);
    },

    addDmaMapping(data: any): Promise<AxiosResponse> {
        return axios.post('/api/dma-mapping', data);
    },

    addAudienceMapping(data: any): Promise<AxiosResponse> {
        return axios.post('/api/audience-mapping', data);
    },

    addDspMacro(data: any): Promise<AxiosResponse> {
        return axios.post('/api/dsp-macro', data);
    },

    addOemProgram(data: any): Promise<AxiosResponse> {
        return axios.post('/api/oem-program', data);
    },

    addOem(data: any): Promise<AxiosResponse> {
        return axios.post('/api/oems', data);
    },

    updateDmaMapping(data: DmaMapping): Promise<AxiosResponse> {
        return axios.put('/api/dma-mapping/' + data.id, data);
    },

    updateBulk(data: any): Promise<AxiosResponse> {
        return axios.patch('/api/dspAdGroupDict/bulkUpdate', data);
    },

    updateBulkCampaigns(data: any): Promise<AxiosResponse> {
        return axios.patch('/api/campaign/bulk-campaigns/update', data);
    },

    syncBulkCampaigns(data: any): Promise<AxiosResponse> {
        return axios.patch('/api/campaign/bulk-campaigns/sync', data);
    },

    updateAdGroupDict(data: DspAdGroupDict): Promise<AxiosResponse> {
        return axios.put('/api/dspAdGroupDict/' + data.id, data);
    },
    updateDspMacro(data: DspMacro): Promise<AxiosResponse> {
        return axios.put('/api/dsp-macro/' + data.id, data);
    },

    updateOemProgram(data: OemProgramDto): Promise<AxiosResponse> {
        return axios.put('/api/oem-program/' + data.id, data);
    },
    updateOem(data: Oem): Promise<AxiosResponse> {
        return axios.put('/api/oems/' + data.id, data);
    },
    updateDspInventorySupply(data: DspInventorySupply): Promise<AxiosResponse> {
        return axios.put('/api/dsp-inventory-supply/' + data.id, data);
    },

    syncCampaign: function (campaignId: number): Promise<AxiosResponse<CampaignSyncResponse>> {
        return axios.post(`/api/campaigns/amazon-dsp/${campaignId}/sync`);
    },

    getConversionByConversionNameSummaryList: function (
        criteria: ConversionSummaryListCriteria,
        config?: AxiosRequestConfig
    ): Promise<AxiosPageableResponse<ConversionByConversionNameSummary>> {
        return axios.post(
            '/api/analytics/conversions/listByConversionName',
            {
                agencyIds: criteria.agencyIds,
                dealerIds: criteria.dealerIds,
                oemIds: criteria?.oemIds || undefined,
                oemProgramIds: criteria?.oemProgramIds || undefined,
                agencyMarketRegions: criteria?.agencyMarketRegions || undefined,
                startDate: Utils.toStartOfDayISOString(criteria.startDate),
                endDate: Utils.toEndOfDayISOString(criteria.endDate),
                mediaType: criteria.mediaType,
                strategyType: criteria.strategyType,
            },
            {
                ...config,
                params: {
                    page: criteria.page,
                    size: criteria.pageSize,
                    sort: `${criteria.sortBy},${criteria.sort}`,
                },
            }
        );
    },

    /**
     * Analytics > App By Spend Summary
     */
    getAppBySpendBySiteNameSummaryList: function (
        criteria: AppBySpendSummaryListCriteria,
        config?: AxiosRequestConfig
    ): Promise<AxiosPageableResponse<AppBySpendBySiteNameSummary>> {
        return axios.post(
            '/api/analytics/appBySpendSummary/listBySiteName',
            {
                agencyIds: criteria.agencyIds,
                dealerIds: criteria.dealerIds,
                oemIds: criteria?.oemIds || undefined,
                oemProgramIds: criteria?.oemProgramIds || undefined,
                agencyMarketRegions: criteria?.agencyMarketRegions || undefined,
                startDate: Utils.toStartOfDayISOString(criteria.startDate),
                endDate: Utils.toEndOfDayISOString(criteria.endDate),
                mediaType: criteria.mediaType,
                strategyType: criteria.strategyType,
            },
            {
                ...config,
                params: {
                    page: criteria.page,
                    size: criteria.pageSize,
                    sort: `${criteria.sortBy},${criteria.sort}`,
                },
            }
        );
    },

    /**
     * Analytics > Reporting
     */
    getReportingMetrics: function (
        criteria: PerformanceCriteria,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse<Record<string, Metric>>> {
        return axios.post(
            '/api/analytics/reporting/metrics',
            {
                ...criteria,
                startDate: Utils.toStartOfDayISOString(criteria.startDate),
                endDate: Utils.toEndOfDayISOString(criteria.endDate),
            },
            { ...config }
        );
    },

    getReportingPerformanceEntities: function (
        criteria: PerformanceEntityCriteria,
        config?: AxiosRequestConfig
    ): Promise<AxiosResponse<PerformanceEntity[]>> {
        return axios.post(
            '/api/analytics/reporting/performanceEntities',
            {
                ...criteria,
                startDate: Utils.toStartOfDayISOString(criteria.startDate),
                endDate: Utils.toEndOfDayISOString(criteria.endDate),
            },
            { ...config }
        );
    },

    /**
     * Ad Tags
     */
    getAdTagImportSources: function (): Promise<AxiosResponse<AdTagImportSource[]>> {
        return axios.get('/api/adTagPlacement/getImportSources');
    },

    getAdTagImportSourceByAgency: function (
        agencyId: number,
        tagType?: string
    ): Promise<AxiosResponse<AdTagImportSource>> {
        const params = tagType ? `?tagType=${tagType}` : '';
        return axios.get(`/api/adTagPlacement/getImportSourceByAgency/${agencyId}${params}`);
    },

    getAdTagIncomingS3Files: function (
        importSourceId: number,
        dspPlatform: string,
        mediaType: string,
        extention: string
    ): Promise<AxiosResponse<FileInfo[]>> {
        return axios.get(
            `/api/adTagPlacement/getIncomingS3Files/${importSourceId}/${dspPlatform}/${mediaType}/${extention}`
        );
    },

    getAdTagProjects: function (
        page: number,
        output: string,
        tagType: keyof typeof AdTagType,
        size = 50
    ): Promise<AxiosPageableResponse<Project>> {
        return axios.get(`/api/adTagPlacement/getAdTagProjects`, {
            ...BYPASS_WAIT_INDICATOR,
            params: { page, size, output, tagType },
        });
    },

    getAdTagPlacements: function (
        agencyId: number | null,
        dealerId: number | undefined,
        page: number,
        size: number,
        sortModel: any,
        bypassWaitIndicator?: boolean
    ): Promise<AxiosPageableResponse<AdTagPlacement>> {
        const sort = sortModel.map((sort: any) => `${sort.field},${sort.sort}`).join(',');
        const config = bypassWaitIndicator ? BYPASS_WAIT_INDICATOR : {};
        return axios.get(`/api/adTagPlacement/placements`, {
            ...config,
            params: { agencyId, dealerId, page, size, sort },
        });
    },

    getTemplates: function (): Promise<AxiosResponse<AdTagTemplate[]>> {
        return axios.get('/api/adTagPlacement/getTemplates');
    },

    createAdTagPlacement: function (placement: AdTagPlacementData): Promise<AxiosResponse<AdTagPlacement>> {
        return axios.post(`/api/adTagPlacement/createAdTagPlacement`, placement);
    },

    deleteAdTagPlacement: function (adTagPlacementId: number): Promise<AxiosResponse> {
        return axios.delete(`/api/adTagPlacement/deleteAdTagPlacement/${adTagPlacementId}`);
    },

    publishAdTagPlacement: function (placementId: number): Promise<AxiosResponse<AdTagPlacement>> {
        return axios.get(`/api/adTagPlacement/publish/${placementId}`);
    },

    /**
     * Ad Server
     */
    getAdServerImportSources: function (): Promise<AxiosResponse<AdServerImportSource[]>> {
        return axios.get('/api/adServer/getImportSources');
    },

    getAdServerIncomingS3Files: function (
        importSourceId: number,
        extention: string
    ): Promise<AxiosResponse<FileInfo[]>> {
        return axios.get(`/api/adServer/getIncomingS3Files/${importSourceId}/${extention}`);
    },

    getAdServerCampaigns: function (dealerId: number): Promise<AxiosResponse<FtCampaign[]>> {
        return axios.get('/api/adServer/' + dealerId + '/campaigns');
    },

    getAdServerPlacements: function (dealerId: number, campaignId: number): Promise<AxiosResponse<FtPlacement[]>> {
        return axios.get(`/api/adServer/${dealerId}/${campaignId}/placements`);
    },

    createAdServerPlacement: function (
        campaignId: number,
        connectedFtPlacement: Pick<
            AdServerPlacement,
            | 'fileNameKey'
            | 'libraryId'
            | 'adServerCampaignId'
            | 'adServerCampaignName'
            | 'placementId'
            | 'placementName'
            | 'fileName'
        >
    ): Promise<AxiosResponse<AdServerPlacement>> {
        return axios.post(`/api/adServer/${campaignId}/createAdServerPlacement`, connectedFtPlacement);
    },

    deleteAdServerPlacement: function (adServerPlacementId: number): Promise<AxiosResponse> {
        return axios.delete(`/api/adServer/deleteAdServerPlacement/${adServerPlacementId}`);
    },

    uploadAdServerCreative: function (
        importSourceId: number,
        adServerPlacementId: number
    ): Promise<AxiosResponse<AdServerPlacement>> {
        return axios.get(`/api/adServer/uploadAdServerCreative/${importSourceId}/${adServerPlacementId}`);
    },

    tryToApproveCreative: function (adServerPlacementId: number): Promise<AxiosResponse<AdServerPlacement>> {
        return axios.get(`/api/adServer/tryToApproveCreative/${adServerPlacementId}`);
    },

    replacePlacementCreative: function (adServerPlacementId: number): Promise<AxiosResponse<AdServerPlacement>> {
        return axios.get(`/api/adServer/replacePlacementCreative/${adServerPlacementId}`);
    },

    /**
     * HAT
     */
    getHatCreativePixels: (advertiserId: number): Promise<AxiosResponse<CreativePixel[]>> => {
        return axios.get(`/api/hat/creativePixels/advertiser/${advertiserId}`);
    },

    getHatCreativePixelByCriteria: (
        advertiserId: number,
        criteria: CreativePixelCriteria
    ): Promise<AxiosResponse<CreativePixel>> => {
        return axios.get(`/api/hat/creativePixels/advertiser/${advertiserId}/getByCriteria`, {
            params: criteria,
        });
    },

    getHatTracking: (advertiserId: number): Promise<AxiosResponse<Tracking>> => {
        return axios.get(`/api/hat/tracking/advertiser/${advertiserId}`);
    },

    getHatConfig: (advertiserId: number): Promise<AxiosResponse<HatConfig>> => {
        return axios.get(`/api/hat/config/advertiser/${advertiserId}`);
    },
    getAllOemPrograms(): Promise<AxiosPageableResponse<OemProgram>> {
        return axios.get(`/api/oem-program/all`);
    },
    getFilteredOemPrograms(
        criteria?: OemProgramCriteria,
        pageable?: Pageable
    ): Promise<AxiosPageableResponse<OemProgram>> {
        return axios.get(`/api/oem-program/filter`, {
            params: {
                ...criteria,
                ...pageable,
                oemIds: criteria?.oemIds?.join(',') || undefined,
                brandIds: criteria?.brandIds?.join(',') || undefined,
                ids: criteria?.ids?.join(',') || undefined,
                agencyIds: criteria?.agencyIds?.join(',') || undefined,
                advertiserIds: criteria?.advertiserIds?.join(',') || undefined,
            },
        });
    },

    listAgencyMarketRegion(criteria?: AgencyMarketRegionCriteria): Promise<AxiosResponse<string[]>> {
        return axios.post('/api/dealers/agencyMarketRegions', criteria);
    },

    // --- Email Test ---

    getEmailTemplates(): Promise<AxiosResponse<EmailTemplateDTO[]>> {
        return axios.get('/api/admin/email-test/templates');
    },

    sendTestEmail(request: SendTestEmailRequest): Promise<AxiosResponse<SendTestEmailResponse>> {
        return axios.post('/api/admin/email-test/send', request);
    },

    previewEmail(request: {
        templateFile: string;
        variables: Record<string, string>;
    }): Promise<AxiosResponse<PreviewEmailResponse>> {
        return axios.post('/api/admin/email-test/preview', request);
    },
};

export { BYPASS_WAIT_INDICATOR, CUSTOM_ERROR_HANDLER };
export default ApiService;
